import { useMemo, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import GetApp from "@mui/icons-material/GetApp";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import { useCombinedFilters } from "../../../../../../hooks/useCombinedFilters";
import { useGetKeyDetections } from "./hooks/useGetKeyDetections";
import { exportCompetitorKeyDetectionCSV, formatDate } from "./utils";
import { NoData } from "../../../../ChartMessages/NoData";
import { hasNoData } from "../../../../utils";
import { InsufficientInfoWrapper } from "../../InsufficientInfoWrapper";
import { TitleWithTooltipInfo } from "../../../../../../components/charts/TitleWithTooltipInfo";
import { CompanyLogo } from "../../../../../../components/CompanyLogo";
import { formatRevenueRange } from "../../../CompanyList/utils";
import {
  formatNumber,
  formatStandardNumber,
} from "../../../../../../utils/numberUtils";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { RecentInstall } from "../../../../../../apis/companySearch/recentInstalls";
import Settings from "@mui/icons-material/Settings";
import { CHART_CONSTANTS } from "../../../constants";

// Type definitions for table rows
type SkeletonKeyDetectionRow = {
  id: string;
  isSkeleton: true;
};

type DataKeyDetectionRow = RecentInstall & {
  id: string;
  isSkeleton: false;
};

type TableKeyDetectionRow = DataKeyDetectionRow | SkeletonKeyDetectionRow;

const { chartTitle, tooltip, baseId } = CHART_CONSTANTS.competitorKeyDetection;

export function CompetitorKeyDetection() {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "revenueMax", sort: "desc" },
  ]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [isChartHovered, setIsChartHovered] = useState(false);

  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { topKeyDetectionsData, isLoading } = useGetKeyDetections({
    companyFilters: combinedFilters || {},
    productIds: competitorsProductIds,
    localizeInstalls,
  });

  // Convert data to table rows with skeleton support
  const rows = useMemo(() => {
    if (isLoading) {
      return Array.from({ length: 5 }).map(
        (_, index) =>
          ({
            id: `skeleton-${index}`,
            isSkeleton: true,
          }) as SkeletonKeyDetectionRow
      );
    }

    // Apply client-side sorting based on sortModel
    const sortedData = [...topKeyDetectionsData];
    if (sortModel.length > 0) {
      const [{ field, sort }] = sortModel;
      sortedData.sort((a, b) => {
        const aValue = a[field as keyof RecentInstall];
        const bValue = b[field as keyof RecentInstall];

        if (field === "firstVerified") {
          const dateA = new Date(aValue as string).getTime();
          const dateB = new Date(bValue as string).getTime();
          return sort === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sort === "asc" ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    }

    return sortedData.map(
      (row) =>
        ({
          ...row,
          id: `${row.productId}-${row.companyName}`,
          isSkeleton: false,
        }) as DataKeyDetectionRow
    );
  }, [isLoading, topKeyDetectionsData, sortModel]);

  // Column definitions
  const columnsDefinition: GridColDef<TableKeyDetectionRow>[] = useMemo(
    () => [
      {
        field: "productName",
        headerName: "Competitor",
        minWidth: 200,
        sortable: true,
        flex: 1,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return (
              <Stack direction="row" alignItems="center" gap={1} flex={1}>
                <Skeleton variant="rounded" width={24} height={24} />
                <Skeleton variant="text" sx={{ flexGrow: 1 }} />
              </Stack>
            );
          }
          const rowData = params.row as DataKeyDetectionRow;
          const vendorUrl = rowData.vendorUrl || "";
          const productName = rowData.productName || "";

          return (
            <Tooltip title={productName} placement="right">
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                overflow="hidden"
              >
                <CompanyLogo
                  domain={vendorUrl}
                  size={24}
                  variant="rounded"
                  fallback={<Settings />}
                />
                <Typography variant="body2" noWrap>
                  {productName}
                </Typography>
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: "companyName",
        headerName: "Recent Detection",
        minWidth: 180,
        flex: 1,
        sortable: true,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="80%" />;
          }
          const rowData = params.row as DataKeyDetectionRow;
          const companyName = rowData.companyName || "";

          return (
            <Tooltip title={companyName} placement="bottom-start">
              <Typography variant="body2" noWrap>
                {companyName}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "revenueMax",
        headerName: "Revenue",
        width: 150,
        sortable: true,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="70%" />;
          }
          const rowData = params.row as DataKeyDetectionRow;
          const revenueRange = formatRevenueRange({
            min: rowData.revenueMin,
            max: rowData.revenueMax,
          });

          return (
            <Tooltip
              title={formatStandardNumber(rowData.revenueMax)}
              placement="bottom-start"
            >
              <Typography variant="body2" textAlign="left">
                {revenueRange}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "intensity",
        headerName: "Intensity",
        width: 110,
        sortable: true,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="60%" />;
          }
          const rowData = params.row as DataKeyDetectionRow;
          return (
            <Typography variant="body2">
              {formatNumber(rowData.intensity || 0)}
            </Typography>
          );
        },
      },
      {
        field: "firstVerified",
        headerName: "First Verified",
        width: 130,
        sortable: true,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="80%" />;
          }
          const rowData = params.row as DataKeyDetectionRow;
          return (
            <Typography variant="body2">
              {formatDate(rowData.firstVerified)}
            </Typography>
          );
        },
      },
    ],
    []
  );

  const handleExportCSV = useCallback(() => {
    if (isLoading || !rows || rows.length === 0) return;

    const dataRows = rows.filter(
      (row) => !row.isSkeleton
    ) as DataKeyDetectionRow[];

    exportCompetitorKeyDetectionCSV({
      headers: columnsDefinition.map((col) => ({
        title: col.headerName || "",
        field: col.field,
      })),
      data: dataRows.map((row) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, isSkeleton, ...csvRow } = row;
        return csvRow;
      }),
    });
  }, [isLoading, rows, columnsDefinition]);

  const noDataReturned = useMemo(
    () =>
      hasNoData(
        isLoading,
        rows.filter((row) => !row.isSkeleton)
      ),
    [isLoading, rows]
  );

  const shouldShowExport = !noDataReturned && !isLoading;

  const getTableTitle = useCallback(() => {
    return (
      <Stack
        direction="row"
        pl={2}
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        onMouseEnter={() => setIsChartHovered(true)}
        onMouseLeave={() => setIsChartHovered(false)}
      >
        <TitleWithTooltipInfo
          chartTitle={chartTitle}
          tooltipTitle={tooltip}
          showInfoIcon={isChartHovered}
        />
        {shouldShowExport && (
          <Tooltip title="Download as CSV">
            <Button
              onClick={handleExportCSV}
              startIcon={<GetApp sx={{ color: "#808080" }} />}
              variant="text"
              disableRipple
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  }, [handleExportCSV, isChartHovered, shouldShowExport]);

  const tableContent = useMemo(() => {
    if (!isLoading && competitorsProductIds.length === 0) {
      return (
        <Box>
          {getTableTitle()}
          <InsufficientInfoWrapper />
        </Box>
      );
    }

    if (noDataReturned) {
      return (
        <Box>
          {getTableTitle()}
          <Box mt={3}>
            <NoData />
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        {getTableTitle()}
        <Box id={`${baseId}-table-wrapper`} sx={{ height: 440, px: 1 }}>
          <DataGrid
            rows={rows}
            columns={columnsDefinition}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="client"
            sortingMode="client"
            sortingOrder={["asc", "desc"]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            getRowId={(row) => row.id}
            disableColumnFilter
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnResize
            disableColumnSelector
            slotProps={{
              pagination: {
                showFirstButton: true,
                showLastButton: true,
              },
              root: {
                id: `${baseId}-table`,
              },
            }}
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.columnSeparator}`]: {
                [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                  display: "none",
                },
              },
              "& .MuiDataGrid-columnHeader": {
                borderBottom: "none !important",
              },
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                "& .MuiIconButton-root": {
                  padding: 0,
                },
                "& .MuiTouchRipple-root": {
                  display: "none",
                },
              },
              "&, [class^=MuiDataGrid]": { border: "none" },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-filler": {
                display: "none",
              },
              ".MuiDataGrid-scrollbarFiller": {
                display: "none",
              },
              "& .MuiDataGrid-scrollbar--vertical": {
                ...lightScrollbarStyle,
              },
              "& .MuiDataGrid-scrollbar--horizontal": {
                ...lightScrollbarStyle,
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  }, [
    rows,
    isLoading,
    paginationModel,
    sortModel,
    columnsDefinition,
    competitorsProductIds,
    noDataReturned,
    getTableTitle,
  ]);

  return (
    <Box
      padding="20px 0px"
      borderRadius="10px"
      border="1px solid #edeff1"
      bgcolor={
        noDataReturned || competitorsProductIds.length === 0
          ? "#edeff1"
          : "#fff"
      }
      height={
        noDataReturned || competitorsProductIds.length === 0
          ? "300px"
          : undefined
      }
    >
      {tableContent}
    </Box>
  );
}
